import React, { useState } from 'react';
import { Headercomponent } from '../components/headercomponent';
import NodeBackground from '../components/nodeBackground';
import DemoModal from '../components/demoModal';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import {
  FaPlaneDeparture,
  FaHome,
  FaShieldAlt,
  FaGlobe,
  FaMapMarkedAlt,
  FaFileAlt,
} from 'react-icons/fa';

// Reuse existing Product page styling where possible:
// container, hero, hero-content, hero-diagram, cta-button,
// platform-overview, overview-cards, overview-feature,
// feature-graphic, feature-text, capability-statement,
// capability-link, card-link, card-link-content, card-left,
// card-right, arrow, how-it-works, cta-section.

Modal.setAppElement('#root');

const ServiceBadge = ({ children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '0.35rem 0.7rem',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: '999px',
      fontSize: '0.8rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      marginBottom: '0.9rem',
      opacity: 0.9,
    }}
  >
    {children}
  </span>
);

const serviceIconMap = {
  'Travel Intelligence': <FaPlaneDeparture />,
  'Property Intelligence': <FaHome />,
  'Protective Intelligence': <FaShieldAlt />,
  'OSINT Intelligence': <FaGlobe />,
  'Geospatial Intelligence': <FaMapMarkedAlt />,
  'Custom Intelligence': <FaFileAlt />,
};

const ServiceGraphic = ({ title, items, onItemClick }) => (
  <div className="service-graphic">
    <div className="service-graphic-header">
      <h3>{title}</h3>
    </div>

    <div className="service-graphic-grid">
      {items.map((item) => (
        <button
          key={item}
          type="button"
          className="service-graphic-item"
          onClick={() => onItemClick?.(item)}
        >
          <div className="service-graphic-left">
            <span className="service-graphic-icon">
              {serviceIconMap[item] || <FaFileAlt />}
            </span>
            <span>{item}</span>
          </div>

          <span className="service-arrow">→</span>
        </button>
      ))}
    </div>
  </div>
);

function Services() {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState(null);
  const [serviceModal, setServiceModal] = useState(null);

  const handleModal = () => setModalIsOpen(!modalIsOpen);

  const services = [
    {
      badge: 'Crime Mapping',
      title: 'Residential Crime Intelligence Reports for Home Buyers and Relocation Decisions',
      text:
        'For clients evaluating homes, neighborhoods, schools, or investment properties, we produce location-based crime intelligence reports using historical crime data, mapped trends, nearby incident patterns, and plain-language analysis.',
      graphicTitle: 'Residential Risk Report',
      items: ['Crime trends near the property', 'Neighborhood comparison', 'Violent and property crime review', 'Buyer-focused risk summary'],
    },
    {
      badge: 'Travel Intelligence',
      title: 'Travel Risk Intelligence for Executives, Families, and Security Teams',
      text:
        'Receive location-specific intelligence before travel decisions are made. We assess crime, protests, transportation risk, online attention, venue exposure, and local conditions so clients can move with better awareness and fewer surprises.',
      graphicTitle: 'Travel Risk Brief',
      items: ['Destination risk overview', 'Hotel and venue area review', 'Route and neighborhood considerations', 'Actionable security recommendations'],
    },
    {
      badge: 'Threat Detection',
      title: 'OSINT Analyst Support for Online Threat Detection',
      text:
        'We combine analyst review with proprietary software to identify relevant online threats, concerning commentary, indirect mentions, and emerging narratives involving executives, companies, families, or protected assets.',
      graphicTitle: 'Threat Monitoring',
      items: ['Proprietary Threat Detection Tool', 'Relevant threat identification', 'Analyst-reviewed findings', 'Escalation-ready reporting'],
    },
    {
      badge: 'Exposure Assessment',
      title: 'Online Exposure Assessments for Executives and High-Net-Worth Clients',
      text:
        'Understand what personal, residential, family, business, and reputational information may be visible online. Our assessments help clients reduce unwanted exposure, identify sensitive public records, and improve personal security posture.',
      graphicTitle: 'Exposure Assessment',
      items: ['Personal information exposure', 'Residential and family exposure', 'Social media and public profile review', 'Risk reduction recommendations'],
    },
    {
      badge: 'Executive Intelligence',
      title: 'Executive Intelligence Program for Dedicated Protective Intelligence Support',
      text:
        'A monthly retainer-based OSINT analyst program for executives, public-facing leaders, and family offices that need continuous protective intelligence without building an in-house capability. Includes exposure monitoring, threat detection, travel intelligence, and recurring analyst reporting.',
      graphicTitle: 'Executive Intelligence Program',
      items: [
        'Dedicated OSINT analyst support',
        'Online exposure and dox monitoring',
        'Travel and event intelligence support',
      ],
    },
    {
      badge: 'Event Support',
      title: 'Event Intelligence Support Before and During High-Visibility Events',
      text:
        'Support private events, public appearances, corporate meetings, and high-profile gatherings with intelligence designed for protective decision-making. We identify online chatter, location risk, protest indicators, and operational concerns that may affect the event environment.',
      graphicTitle: 'Event Support Package',
      items: ['Pre-event risk assessment', 'Online monitoring support', 'Venue and surrounding area review', 'Post-event intelligence summary'],
    },
    {
      badge: 'Custom OSINT',
      title: 'Custom Intelligence Reports for Specific Questions',
      text:
        'Some decisions require more than a standard report. We build tailored OSINT and risk intelligence products to answer specific questions involving crime, travel, public exposure, online activity, locations, organizations, or persons of concern.',
      graphicTitle: 'Custom Analysis',
      items: ['Defined intelligence question', 'Open-source collection', 'Analyst assessment', 'Executive-ready deliverable'],
    },
  ];

  const serviceDetails = {
    'Dedicated OSINT analyst support': {
      title: 'Dedicated OSINT Analyst Support',
      need:
        'Provides direct access to an assigned OSINT analyst who supports ongoing intelligence requirements, exposure monitoring, and investigative questions related to executive or organizational risk.',
      includes: [
        'Assigned OSINT analyst support',
        'Direct intelligence requests and tasking',
        'Contextual analysis of emerging risks',
        'Support for executive protection operations'
      ],
      for:
        'Executives, UHNW individuals, family offices, and security teams requiring dedicated intelligence support.'
    },

    'Online exposure and dox monitoring': {
      title: 'Online Exposure & Dox Monitoring',
      need:
        'Continuously monitors public-facing information sources to identify exposure risks, doxxing activity, and sensitive personal or organizational data appearing online.',
      includes: [
        'Monitoring of data broker and people-search exposure',
        'Detection of doxxing attempts or exposure leaks',
        'Alerting on new sensitive information disclosures',
        'Analyst review of exposure risk changes'
      ],
      for:
        'High-profile individuals, executives, and families with elevated exposure risk.'
    },

    'Travel and event intelligence support': {
      title: 'Travel & Event Intelligence Support',
      need:
        'Provides intelligence support for travel and high-visibility events, including environmental risk, local threat context, and situational awareness planning.',
      includes: [
        'Destination and route risk context',
        'Event and venue threat environment review',
        'Local incident and protest indicator monitoring',
        'Pre-movement intelligence briefings'
      ],
      for:
        'Executive protection teams, corporate security, and individuals traveling or attending high-visibility events.'
    },
    'Destination risk overview': {
      title: 'Destination Risk Overview',
      need: 'Travel conditions can vary significantly by city, neighborhood, season, and current events. This review helps clients understand the broader environment before they arrive.',
      includes: [
        'Local crime and safety context',
        'Recent unrest, protest, or disruption indicators',
        'Neighborhood-level considerations',
        'Relevant local conditions affecting movement or visibility',
      ],
      for: 'Executives, families, travelers, and security teams planning movement into an unfamiliar city or region.',
    },

    'Hotel and venue area review': {
      title: 'Hotel and Venue Area Review',
      need: 'A hotel or venue can look appropriate online while still sitting near avoidable risk areas. This review helps clients understand the immediate area around key locations.',
      includes: [
        'Crime patterns near the hotel or venue',
        'Nearby transportation, parking, and pedestrian considerations',
        'Surrounding neighborhood context',
        'Arrival and departure concerns',
      ],
      for: 'Travelers, executive protection teams, event planners, and families selecting lodging or event locations.',
    },

    'Route and neighborhood considerations': {
      title: 'Route and Neighborhood Considerations',
      need: 'Risk can change from block to block. This review helps clients understand movement considerations between airports, hotels, venues, residences, and meetings.',
      includes: [
        'Route-adjacent crime and safety context',
        'Neighborhood transitions along likely travel corridors',
        'Areas worth avoiding or approaching with added awareness',
        'Practical movement considerations',
      ],
      for: 'Security teams, private travelers, executives, and families moving through unfamiliar areas.',
    },

    'Actionable security recommendations': {
      title: 'Actionable Security Recommendations',
      need: 'Intelligence is only useful if it supports a decision. Recommendations translate findings into practical considerations for movement, lodging, timing, and security posture.',
      includes: [
        'Plain-language risk summary',
        'Practical mitigation considerations',
        'Decision support for travel planning',
        'Recommendations tied directly to observed findings',
      ],
      for: 'Clients who need clear next steps, not a generic risk overview.',
    },

    'Proprietary Threat Detection Tool': {
      title: 'Proprietary Threat Detection Tool',
      need: 'Relevant threat indicators are often buried in comment sections, forums, and smaller online communities. Our platform helps surface concerning content faster than manual review alone.',
      includes: [
        'Collection across relevant online sources',
        'Natural language review for threat indicators',
        'Prioritized findings for analyst review',
        'Support for protective intelligence workflows',
      ],
      for: 'Executive protection teams, corporate security teams, analysts, and organizations monitoring public-facing individuals.',
    },

    'Relevant threat identification': {
      title: 'Relevant Threat Identification',
      need: 'Most online content is noise. This service focuses on identifying content that may be relevant to physical security, reputational risk, concerning behavior, or escalation indicators.',
      includes: [
        'Review of direct and indirect mentions',
        'Escalations of concerning content',
        'Identification of concerning narratives',
        'Filtering of irrelevant or low-value content',
        'Analyst judgment applied to findings',
      ],
      for: 'Protective intelligence teams, corporate security, public figures, and high-profile families.',
    },

    'Analyst-reviewed findings': {
      title: 'Analyst-Reviewed Findings',
      need: 'Automated alerts are not enough. Analyst review helps determine what matters, why it matters, and whether the information should be escalated.',
      includes: [
        'Human review of surfaced information',
        'Contextual assessment of concerning content',
        'Finding summaries written for decision-makers',
        'Reduction of false positives and irrelevant noise',
      ],
      for: 'Teams that need defensible intelligence products rather than raw alert feeds.',
    },

    'Escalation-ready reporting': {
      title: 'Escalation-Ready Reporting',
      need: 'When something concerning is identified, teams need reporting that can be shared quickly and clearly. Escalation-ready reporting organizes key facts, context, and recommended considerations.',
      includes: [
        'Clear summary of the concern',
        'Source context and relevant indicators',
        'Assessment of why the item matters',
        'Recommended next steps or considerations',
      ],
      for: 'Security teams, executives, legal teams, and protective decision-makers.',
    },

    'Pre-event risk assessment': {
      title: 'Pre-Event Risk Assessment',
      need: 'Events create temporary visibility and concentrated exposure. A pre-event assessment helps identify issues before people arrive on site.',
      includes: [
        'Venue and surrounding area review',
        'Relevant crime and safety context',
        'Online attention or protest indicators',
        'Operational considerations before the event',
      ],
      for: 'Event security teams, executive protection details, corporate teams, and private event hosts.',
    },

    'Online monitoring support': {
      title: 'Online Monitoring Support',
      need: 'Online discussion can shift before and during high-visibility events. Monitoring support helps identify relevant chatter, emerging narratives, or concerning attention.',
      includes: [
        'Public online source monitoring',
        'Review of relevant comments and discussion',
        'Identification of escalation indicators',
        'Reporting of items requiring attention',
      ],
      for: 'Security teams supporting public appearances, corporate events, private gatherings, or high-profile meetings.',
    },

    'Venue and surrounding area review': {
      title: 'Venue and Surrounding Area Review',
      need: 'The area around a venue can affect arrival, departure, parking, foot movement, and overall security posture.',
      includes: [
        'Nearby crime and safety context',
        'Parking, access, and movement considerations',
        'Surrounding neighborhood review',
        'Location-specific concerns affecting event planning',
      ],
      for: 'Event planners, security teams, executive protection details, and organizations hosting high-visibility events.',
    },

    'Post-event intelligence summary': {
      title: 'Post-Event Intelligence Summary',
      need: 'After an event, teams may need a concise record of relevant issues, online attention, or lessons learned for future planning.',
      includes: [
        'Summary of relevant findings',
        'Notable online or location-based issues',
        'Operational takeaways',
        'Recommendations for future events',
      ],
      for: 'Organizations that run repeated events or need documentation for stakeholders after an event.',
    },
    'Crime trends near the property': {
      title: 'Crime Trends Near the Property',
      need:
        'Historical crime patterns provide important context when evaluating a residence, investment property, or relocation destination. Understanding what has occurred near a location over time helps clients identify recurring issues and compare neighborhoods beyond general city crime statistics.',

      includes: [
        'Historical crime trends surrounding the property',
        'Violent, property, and quality-of-life crime analysis',
        'Neighborhood comparisons',
        'Plain-language analyst assessment',
        'Interactive maps and visualizations',
      ],

      for:
        'Home buyers, real estate professionals, relocation clients, corporate housing teams, executive protection teams, and individuals evaluating unfamiliar neighborhoods.',
    },

    'Defined intelligence question': {
      title: 'Custom Intelligence Analysis',
      need:
        'Some questions cannot be answered with a standard report. Custom intelligence products are built around a specific client objective, combining open-source research, geospatial analysis, crime data, and analyst judgment to provide decision-focused intelligence.',

      includes: [
        'Clearly defined intelligence objective',
        'Tailored OSINT collection',
        'Analysis from multiple public data sources',
        'Executive-ready written assessment',
        'Actionable findings and recommendations',
      ],

      for:
        'Corporate security teams, attorneys, executive protection professionals, investigators, businesses, and private clients requiring intelligence for a unique decision or operational need.',
    },
  };

  return (
    <>
      <Headercomponent />

      <div className="container">
        <div className="hero">
          <NodeBackground />
          <div className="hero-content">
            <h1>Know the Risk Before You Act</h1>
            <p>
              Intelligence reports and Open-Source Intelligence (OSINT) support for businesses, executives, families, travelers, real estate buyers, and security teams who inform high-value decisions on validated intelligence.
            </p>
            <button className="cta-button" onClick={handleModal}>
              Request an Intelligence Assessment
            </button>
          </div>

          <div className="hero-diagram">
            <ServiceGraphic
              title="Intelligence Product Line"
              items={[
                'Travel Intelligence',
                'Residential & Property Intelligence',
                'Protective Intelligence',
                'Geospatial Intelligence',
                'Custom Intelligence',
              ]}
              onItemClick={(item) => {
                const heroMap = {
                  'Travel Intelligence': 'Destination risk overview',
                  'Residential & Property Intelligence': 'Crime trends near the property',
                  'Protective Intelligence': 'Relevant threat identification',
                  'OSINT Intelligence': 'Proprietary Threat Detection Tool',
                  'Geospatial Intelligence': 'Venue and surrounding area review',
                  'Custom Intelligence': 'Defined intelligence question',
                };

                const detailKey = heroMap[item];

                if (serviceDetails[detailKey]) {
                  setServiceModal(serviceDetails[detailKey]);
                }
              }}
            />
          </div>
        </div>

        <section className="platform-overview">
          <h2>Intelligence Support Built Around Your Risk Decisions</h2>
          <p>
            Our services combine professional OSINT collection, crime analysis, location intelligence, and analyst judgment to help clients make safer, more informed decisions. Built by a former Washington, D.C. police officer and OSINT intelligence analyst, our intelligence products are designed to answer real-world security and risk questions.
          </p>

          <div className="overview-cards">
            {services.map((service, index) => (
              <div
                className={`overview-feature ${index === 1 || index === 3 || index === 5 ? 'overview-feature-reverse' : ''}`}
                key={service.title}
              >
                <div className="feature-graphic">
                  {index === 0 ? (
                    <button
                      type="button"
                      className="service-preview-button"
                      onClick={() =>
                        setPreviewModal({
                          title: 'Residential Crime Intelligence Report',
                          desktop: '/neighborhood_report.png',
                          mobile: '/neighborhood_report.png',
                        })
                      }
                    >
                      <img
                        src="/neighborhood_report.png"
                        alt="Residential Crime Intelligence Report"
                        className="service-preview-image"
                      />
                    </button>
                  ) : index === 3 ? (
                    <button
                      type="button"
                      className="service-preview-button"
                      onClick={() =>
                        setPreviewModal({
                          title: 'Online Exposure Assessment',
                          desktop: '/OEA_image.png',
                          mobile: '/OEA_sources_vert.png',
                        })
                      }
                    >
                      <img
                        src="/OEA_image.png"
                        alt="Online Exposure Assessment"
                        className="service-preview-image service-preview-image-contained"
                      />
                    </button>
                  ) : index === 5 ? (
                    <button
                      type="button"
                      className="service-preview-button"
                      onClick={() =>
                        setPreviewModal({
                          title: 'Custom Intelligence Cycle',
                          desktop: '/intelligence_cycle_desktop.png',
                          mobile: '/intelligence_cycle.png',
                        })
                      }
                    >
                      <picture>
                        <source
                          media="(max-width: 768px)"
                          srcSet="/intelligence_cycle.png"
                        />
                        <img
                          src="/intelligence_cycle_desktop.png"
                          alt="Online Exposure Assessment"
                          className="service-preview-image"
                        />
                      </picture>
                    </button>
                  ) : (
                    <ServiceGraphic
                      title={service.graphicTitle}
                      items={service.items}
                      onItemClick={(item) => {
                        const detail = serviceDetails[item];
                        if (detail) {
                          setServiceModal(detail);
                        }
                      }}
                    />
                  )}
                </div>
                <div className="feature-text">
                  <ServiceBadge>{service.badge}</ServiceBadge>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>

                  {/* 
                  {index === 4 && (
                    <Link to="/risk-explorer" className="capability-link card-link">
                      <div className="card-link-content">
                        <div className="card-left">
                          <h4>Explore the DC Crime Map</h4>
                          <p>Preview how mapped crime data can support property, travel, and neighborhood risk decisions.</p>
                        </div>
                        <div className="card-right"><span className="arrow">→</span></div>
                      </div>
                    </Link>
                  )} */}
                </div>
              </div>
            ))}
          </div>
        </section>


        <section className="process-deliverables-section">
          <div className="process-column">
            <span className="section-kicker">Our Process</span>
            <h2>From Question to Intelligence Product</h2>
            <p>
              Every project starts with the decision you need to make. We define the question,
              collect and analyze relevant information, then deliver a clear intelligence product
              built for action.
            </p>

            <div className="process-steps">
              <div className="process-step">
                <span>01</span>
                <div>
                  <h3>Define the Question</h3>
                  <p>We clarify the person, property, location, event, or concern requiring intelligence support.</p>
                </div>
              </div>

              <div className="process-step">
                <span>02</span>
                <div>
                  <h3>Collect & Analyze</h3>
                  <p>We review relevant open-source, crime, location, exposure, and contextual information.</p>
                </div>
              </div>

              <div className="process-step">
                <span>03</span>
                <div>
                  <h3>Deliver the Assessment</h3>
                  <p>You receive a concise intelligence report or briefing with findings, context, and recommendations.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="deliverables-column">
            <span className="section-kicker">Deliverables</span>
            <h2>What you Can Expect</h2>
            <p>Each assessment is tailored to the client's objectives so that we consistently deliver the findings and context needed to understand risk and take informed action. </p>

            <div className="deliverables-grid">
              <div className="deliverable-card">
                <h3>Executive Summary</h3>
                <p>A concise overview of the most important findings.</p>
              </div>

              <div className="deliverable-card">
                <h3>Maps & Visuals</h3>
                <p>Crime maps, exposure graphics, timelines, or other visuals where useful.</p>
              </div>

              <div className="deliverable-card">
                <h3>Analyst Findings</h3>
                <p>Clear explanation of what was identified and why it matters.</p>
              </div>

              <div className="deliverable-card">
                <h3>Risk Assessment</h3>
                <p>Practical risk context based on available information.</p>
              </div>

              <div className="deliverable-card">
                <h3>Recommendations</h3>
                <p>Actionable considerations to support safer decision-making.</p>
              </div>

              <div className="deliverable-card">
                <h3>Executive-Ready Report</h3>
                <p>Delivered in a format suitable for clients, families, or security teams.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Get Intelligence Before the Decision</h2>
          <p>
            Whether you are planning travel, evaluating a residence, preparing for an event, or protecting an executive, Weathervane Intel can provide targeted intelligence support built around the decision in front of you.
          </p>
          <button className="cta-button" onClick={handleModal}>Request a Consultation</button>
        </section>

        <DemoModal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)} />

        <Modal
          isOpen={!!previewModal}
          onRequestClose={() => setPreviewModal(null)}
          className="report-preview-modal"
          overlayClassName="report-preview-overlay"
        >
          <div className="report-preview-header">
            <h3>{previewModal?.title}</h3>

            <button
              type="button"
              className="report-preview-close"
              onClick={() => setPreviewModal(null)}
            >
              ×
            </button>
          </div>

          {previewModal && (
            <picture className="report-preview-picture">
              <source
                media="(max-width: 768px)"
                srcSet={previewModal.mobile}
              />
              <img
                src={previewModal.desktop}
                alt={previewModal.title}
                className="report-preview-full-image"
              />
            </picture>
          )}
        </Modal>

        <Modal
          isOpen={!!serviceModal}
          onRequestClose={() => setServiceModal(null)}
          className="service-detail-modal"
          overlayClassName="report-preview-overlay"
        >
          {serviceModal && (
            <>
              <div className="report-preview-header">
                <h3>{serviceModal.title}</h3>

                <button
                  type="button"
                  className="report-preview-close"
                  onClick={() => setServiceModal(null)}
                >
                  ×
                </button>
              </div>

              <div className="service-detail-content">
                <section>
                  <h4>Why It Matters</h4>
                  <p>{serviceModal.need}</p>
                </section>

                <section>
                  <h4>What This Includes</h4>
                  <ul>
                    {serviceModal.includes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h4>Who It Is For</h4>
                  <p>{serviceModal.for}</p>
                </section>
              </div>
            </>
          )}
        </Modal>
      </div>
      <Footer />
    </>
  );
}

export default Services;
